import SystemDashboardWidget from '@/models/SystemDashboardWidget'
import SystemDashboardAction from '@/models/SystemDashboardAction'
import SelectOption from '@/models/interface/SelectOption'
import {
  DeliveryGroupingKey,
  DeliveryGroupRow,
  DeliveryOverviewResponse,
  fetchDeliveryOverview,
} from './deliveryContract'

// Suffix marking advertisers whose Disney account status is approved.
const STAR = ' ★'

/**
 * KAN-195 — Reusable month-to-date breakdown table (rendered as a `table-group`
 * so the bucket columns get a grouped two-row header).
 *
 * Columns: Disney MLB, Disney Non-MLB, Non-Disney — each as impressions + % of
 * the row total — plus a Total column. Rows are the grouping (industry, sales
 * channel, or Disney account status) selected via `dataKey`.
 *
 * NOTE on field keys: TableGroup splits sub-field keys on `_` into
 * [property, itemKey], so the bucket property names here are single tokens
 * (`disneymlb`, `disneynonmlb`, `nondisney`) — do not add underscores to them.
 */
export default class DeliveryBreakdownTable extends SystemDashboardWidget {
  public title: string = 'Delivery Breakdown'

  public key: string = 'delivery-breakdown'

  public view: string = 'table-group'

  public size = {
    lg: 8,
    md: 12,
  }

  public noPagination: boolean = true

  public bordered: boolean = true

  public dataKey: DeliveryGroupingKey = 'by_industry'

  public firstLabel: string = 'Group'

  // Dashboard-wide Non-Disney toggle (broadcast from the module's filter row).
  // When hidden, the Non-Disney columns disappear and Total/% are Disney-only;
  // when shown, Total/% cover the full line delivery including Non-Disney.
  public filters: any = {
    show_nondisney: 'hide',
  }

  // Advertiser table only (starToggle arg): star approved Disney accounts and
  // let the user narrow the rows to starred / non-starred. Targets
  // internalFilters so changing it re-runs the mapper for just this widget.
  public starToggle: boolean = false

  public internalFilters: any = {}

  constructor(args: any = null) {
    super()

    if (args && args.title) this.title = args.title
    if (args && args.key) this.key = args.key
    if (args && args.dataKey) this.dataKey = args.dataKey
    if (args && args.firstLabel) this.firstLabel = args.firstLabel
    if (args && args.size) this.size = args.size

    if (args && args.starToggle) {
      this.starToggle = true
      this.internalFilters = { starred: 'all' }

      const action = new SystemDashboardAction()
      action.type = 'radio'
      action.target = 'internalFilters'
      action.key = 'starred'
      action.label = 'Disney Account Status'
      // Option values are disney_account_status values ('all' shows everything).
      action.options = [
        new SelectOption('All', 'all'),
        new SelectOption('★ Approved', 'approved'),
        new SelectOption('Blocklist Exception', 'blocklist_exception'),
      ]
      this.rightCol = [action]
    }

    // Default sort: alphabetical by label for every table. Clicking a column
    // header re-sorts (see dataMapper).
    this.orderBy = 'label'
    this.sortDesc = false

    this.fields = [
      {
        key: 'label', label: '', colspan: 1, thClass: 'text-left align-middle',
      },
      {
        key: 'disneymlb', label: 'MLB OOM', colspan: 2, thClass: 'text-center align-middle',
      },
      {
        key: 'disneynonmlb',
        label: 'Non-MLB OOM',
        colspan: 2,
        thClass: 'text-center align-middle',
      },
      {
        key: 'nondisney',
        label: 'Shifted off Disney',
        colspan: 2,
        thClass: 'text-center align-middle',
      },
      {
        key: 'total', label: 'Total', colspan: 1, thClass: 'text-center align-middle',
      },
    ]

    this.subFields = [
      {
        key: 'label',
        label: this.firstLabel,
        show: true,
        headerSortable: true,
        footer: { label: 'Total' },
        thClass: 'text-left',
        cellClass: 'text-left font-weight-bold',
      },
      ...this.bucketSubFields('disneymlb'),
      ...this.bucketSubFields('disneynonmlb'),
      ...this.bucketSubFields('nondisney'),
      {
        key: 'total_impressions',
        label: 'Imps',
        type: 'numeral',
        show: true,
        headerSortable: true,
        thClass: 'text-center',
        tdClass: 'text-center font-weight-bold',
      },
    ]
  }

  /** Footnote explaining the starred Disney account statuses (advertiser table). */
  public get notes(): string {
    if (!this.starToggle) {
      return ''
    }

    return `<div class="text-muted small mt-2">
      <div><strong>★ Approved</strong> — Cleared by Disney to run; counts as additional revenue.</div>
      <div><strong>Blocklist Exception</strong> — An existing Disney advertiser that Disney has specifically permitted us to run campaigns for.</div>
    </div>`
  }

  /** Hide the Non-Disney group header while the dashboard toggle is off. */
  public showFields(_: SystemDashboardWidget, fields: any) {
    const hideNonDisney = this.filters.show_nondisney !== 'show'
    return fields.filter(
      (f: any) => f.show !== false && !(hideNonDisney && f.key === 'nondisney'),
    )
  }

  /** Hide the Non-Disney sub-columns while the dashboard toggle is off. */
  public showSubFields(_: SystemDashboardWidget, fields: any) {
    const hideNonDisney = this.filters.show_nondisney !== 'show'
    return fields.filter(
      (f: any) => f.show !== false && !(hideNonDisney && f.key.startsWith('nondisney')),
    )
  }

  private bucketSubFields(bucket: string) {
    return [
      {
        key: `${bucket}_impressions`,
        label: 'Imps',
        type: 'numeral',
        show: true,
        headerSortable: true,
        thClass: 'text-center',
        tdClass: 'text-center',
      },
      {
        key: `${bucket}_pct`,
        label: '%',
        type: 'percentage',
        show: true,
        headerSortable: true,
        // Per-row % is share of that row's total; a column total would be
        // misleading, so the footer cell renders a dash.
        total: 'none',
        thClass: 'text-center',
        tdClass: 'text-center text-muted',
      },
    ]
  }

  public async dataSource(): Promise<DeliveryGroupRow[]> {
    const res: DeliveryOverviewResponse = await fetchDeliveryOverview(false, this.filters.month)
    return res[this.dataKey]
  }

  protected dataMapper(rows: DeliveryGroupRow[]): Promise<any> {
    // Total and % follow the Non-Disney toggle: Disney-only when hidden, full
    // line delivery (Disney + Non-Disney) when shown.
    const showNonDisney = this.filters.show_nondisney === 'show'
    const rowTotal = (row: DeliveryGroupRow) =>
      row.disneymlb + row.disneynonmlb + (showNonDisney ? row.nondisney : 0)

    const isStarred = (row: DeliveryGroupRow) => row.disney_account_status === 'approved'
    const statusFilter = this.starToggle ? this.internalFilters.starred : 'all'

    const items = rows
      // Drop rows with nothing in the visible buckets.
      .filter(row => rowTotal(row) > 0)
      // Status toggle (advertiser table): narrow to the selected Disney
      // account status; 'all' shows everything.
      .filter(row => statusFilter === 'all' || row.disney_account_status === statusFilter)
      .map(row => {
        const total = rowTotal(row)
        const bucket = (value: number) => [
          { key: 'impressions', value },
          { key: 'pct', value: total ? value / total : 0 },
        ]

        return {
          label: this.starToggle && isStarred(row) ? `${row.label}${STAR}` : row.label,
          disneymlb: bucket(row.disneymlb),
          disneynonmlb: bucket(row.disneynonmlb),
          nondisney: bucket(row.nondisney),
          total: [{ key: 'impressions', value: total }],
        }
      })

    const sorted = this.sortItems(items)
    this.records = sorted.length

    return Promise.resolve(sorted)
  }

  /**
   * Sort the built rows by the active column (this.orderBy / this.sortDesc).
   * Column keys are either 'label' or `<bucket>_<metric>` (e.g.
   * 'disneymlb_impressions'), matching the sub-field keys; clicking a header
   * updates orderBy/sortDesc and re-runs this mapper.
   */
  private sortItems(items: any[]): any[] {
    const key = String(this.orderBy)

    const valueOf = (item: any) => {
      if (key === 'label') {
        // Ignore the approved-account star so alphabetical order is by name.
        return String(item.label).replace(STAR, '')
      }
      const [prop, metric] = key.split('_')
      const cells = item[prop]
      const cell = Array.isArray(cells) ? cells.find((c: any) => c.key === metric) : null
      return cell ? cell.value : 0
    }

    return [...items].sort((a, b) => {
      const av = valueOf(a)
      const bv = valueOf(b)
      const cmp = typeof av === 'string' || typeof bv === 'string'
        ? String(av).localeCompare(String(bv))
        : (av as number) - (bv as number)
      return this.sortDesc ? -cmp : cmp
    })
  }
}
